import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowRight, Leaf, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { SEO } from '@/components/SEO';
import { SocialShare } from '@/components/SocialShare';
import digitalDetoxHero from '@/assets/digital-detox-hero.jpg';
const Blog = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState<"date" | "readTime" | "title">("date");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleArticleClick = (slug: string) => {
    if (slug === 'romo-relief' || slug === 'overstimulation' || slug === 'offline-gatherings' || slug === 'lost-art-of-walking') {
      navigate(`/articles/${slug}`);
    } else {
      navigate(`/blog/${slug}`);
    }
  };

  // Articles with dates spread over last 3-4 months (July-October 2025)
  const articles = [{
    title: "Harmony, Not Rejection: How Mystic Forest Sees Technology and AI",
    excerpt: "Technology is not good or bad; nor is it neutral. Exploring our pro-human philosophy on digital minimalism, AI, and the gentle nostalgia for proportionate living.",
    image: "/lovable-uploads/typewriter-harmony.jpg",
    date: "November 1, 2025",
    readTime: "11 min read",
    author: "Piotr",
    categories: ["Digital Minimalism", "Technology"],
    slug: "harmony-not-rejection"
  }, {
    title: "The Geography of Wonder: Lessons from Robert Macfarlane's The Wild Places",
    excerpt: "The wild places are there, if we know how to look. Exploring Macfarlane's radical insight that wildness is not a destination — it's a way of paying attention.",
    image: "/lovable-uploads/kayak-fjord-macfarlane.jpg",
    date: "October 30, 2025",
    readTime: "12 min read",
    author: "Piotr",
    categories: ["Nature Philosophy", "Mindfulness"],
    slug: "geography-of-wonder"
  }, {
    title: "Beyond the Escape Room: Why Authentic Experiences Create Stronger Teams",
    excerpt: "When people are financially invested, they want a return. When emotionally invested, they want to contribute. Exploring why authentic experiences create stronger teams.",
    image: "/lovable-uploads/team-building-bw.jpg",
    date: "October 29, 2025",
    readTime: "10 min read",
    author: "Piotr",
    categories: ["Leadership", "Team Building"],
    slug: "authentic-experiences"
  }, {
    title: "When 'More' Is No Longer Enough",
    excerpt: "On the quiet shift from achievement to peace — a founding philosophy of Mystic Forest exploring the transformation from endless striving to meaningful presence.",
    image: "/lovable-uploads/when-more-is-no-longer-enough.jpg",
    date: "June 15, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Philosophy", "Mindfulness"],
    slug: "when-more-is-no-longer-enough",
    isFounding: true
  }, {
    title: "Rediscovering the Wild Within — Lessons from Ernest Thompson Seton's Two Little Savages",
    excerpt: "Over a century ago, Ernest Thompson Seton wrote Two Little Savages — a forgotten classic that speaks to our modern hunger for reconnection with nature and authentic presence.",
    image: "/lovable-uploads/two-little-savages-book.jpg",
    date: "October 24, 2025",
    readTime: "12 min read",
    author: "Piotr",
    categories: ["Mindful Living", "Nature Connection"],
    slug: "two-little-savages"
  }, {
    title: "The Science of Stillness — How Nature Restores the Mind",
    excerpt: "Discover how Attention Restoration Theory explains why nature heals our exhausted minds and restores our ability to focus in an overstimulated world.",
    image: "/lovable-uploads/nature-stillness-restoration.jpg",
    date: "October 18, 2025",
    readTime: "7 min read",
    author: "Piotr",
    categories: ["Nature Practices", "Wellness"],
    slug: "science-of-stillness"
  }, {
    title: "The Gentle Rebellion of Slow Parenting: Rediscovering Presence with Our Children",
    excerpt: "In a world that glorifies productivity, choosing simplicity in parenting is a quiet rebellion. Discover the science and wisdom behind slow parenting.",
    image: "/lovable-uploads/slow-parenting-family.jpg",
    date: "October 11, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Lifestyle"],
    slug: "slow-parenting"
  }, {
    title: "Returning to Walden — Why Thoreau's Woods Still Matter",
    excerpt: "Nearly two centuries after Thoreau's experiment, his decision to live deliberately feels more radical than ever. Exploring how Walden's wisdom speaks to our digital age.",
    image: "/lovable-uploads/walden-cabin.jpg",
    date: "October 4, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Philosophy", "Mindfulness"],
    slug: "returning-to-walden"
  }, {
    title: "The Shadow Side of \"Good Vibes Only\": Rethinking the Modern Wellness Culture",
    excerpt: "Wellness culture loves light. But somewhere along the way, the pursuit of wellness has turned into an obligation to feel good. Exploring toxic positivity and the path to authentic emotional health.",
    image: "/lovable-uploads/shadow-side-good-vibes.jpg",
    date: "September 27, 2025",
    readTime: "12 min read",
    author: "Piotr",
    categories: ["Wellness", "Mental Health"],
    slug: "shadow-side-good-vibes"
  }, {
    title: "The Lost Art of Walking: Why Movement Heals the Mind",
    excerpt: "Exploring why walking is one of the most powerful tools for mental and physical health — from ancient philosophy to modern neuroscience.",
    image: "/lovable-uploads/walking-forest-road.jpg",
    date: "September 20, 2025",
    readTime: "10 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Wellness"],
    slug: "lost-art-of-walking"
  }, {
    title: "The Quiet Rebellion: Why We're Choosing to Be Offline — Together",
    excerpt: "Exploring the cultural rise of intentional offline gatherings — a growing movement towards analog, in-person experiences as a counterbalance to digital saturation.",
    image: "/lovable-uploads/offline-gathering-hands.jpg",
    date: "September 13, 2025",
    readTime: "12 min read",
    author: "Piotr",
    categories: ["Digital Wellness", "Community"],
    slug: "offline-gatherings"
  }, {
    title: "The Art of Doing One Thing at a Time",
    excerpt: "Discover how monotasking — the practice of doing one thing fully and deeply — can transform your focus, reduce stress, and help you reclaim presence in a world of constant distraction.",
    image: "/lovable-uploads/focus-succulents.jpg",
    date: "September 6, 2025",
    readTime: "9 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Digital Wellness"],
    slug: "art-of-doing-one-thing"
  }, {
    title: "FOMO Is Dead: The Quiet Rise of ROMO — Relief of Missing Out",
    excerpt: "Discover ROMO—the relief of missing out—and learn how conscious absence can help you reclaim presence, creativity, and peace in a noisy digital world.",
    image: "/lovable-uploads/romo-forest.jpg",
    date: "August 30, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Digital Wellness", "Mindfulness"],
    slug: "romo-relief"
  }, {
    title: "The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now",
    excerpt: "Discover how digital minimalism can transform your relationship with technology and help you reclaim focus, solitude, and intentionality in an overwhelmed world.",
    image: "/lovable-uploads/digital-minimalism-hero.jpg",
    date: "August 23, 2025",
    readTime: "10 min read",
    author: "Piotr",
    categories: ["Digital Wellness", "Mindfulness"],
    slug: "digital-minimalism"
  }, {
    title: "Silence – The Absence of Noise That Can Change Life",
    excerpt: "Explore the science of silence and learn practical ways to cultivate quiet moments that reduce stress and enhance clarity.",
    image: "/lovable-uploads/1c01990f-9d38-49ae-9b00-58eea417b3b7.png",
    date: "August 16, 2025",
    readTime: "4 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Wellness"],
    slug: "silence-absence-noise"
  }, {
    title: "Why We Need Community – Tribe as a Source of Strength",
    excerpt: "Explore the vital role of community in human well-being and discover practical ways to build meaningful connections in modern life.",
    image: "/lovable-uploads/97b23649-1a4e-41c6-a0e7-bfc8b650b671.png",
    date: "August 9, 2025",
    readTime: "4 min read",
    author: "Piotr",
    categories: ["Community", "Lifestyle"],
    slug: "why-we-need-community"
  }, {
    title: "Offline Time with Children – Why It Matters",
    excerpt: "Learn why limiting screen time and creating offline moments strengthens family bonds and supports healthy child development.",
    image: "/lovable-uploads/e7cdbc05-bdac-4206-b40e-fa62fbadead6.png",
    date: "August 2, 2025",
    readTime: "5 min read",
    author: "Piotr",
    categories: ["Family", "Digital Wellness"],
    slug: "offline-time-children"
  }, {
    title: "Food as Care – The Power of Shared Meals",
    excerpt: "Explore how cooking and eating together creates meaningful connections, fosters mindfulness, and strengthens community bonds.",
    image: "/lovable-uploads/9a960794-7883-47e3-83c4-865469e41cc7.png",
    date: "July 26, 2025",
    readTime: "4 min read",
    author: "Piotr",
    categories: ["Community", "Lifestyle"],
    slug: "food-as-care"
  }, {
    title: "The Power of Rituals – Why We Need Simple Ceremonies in Life",
    excerpt: "Explore how everyday rituals create meaning, reduce anxiety, and strengthen social bonds in our fast-paced world.",
    image: "/lovable-uploads/6b932101-db8c-4e5e-bb2d-87f4128dae97.png",
    date: "July 19, 2025",
    readTime: "4 min read",
    author: "Piotr",
    categories: ["Lifestyle", "Spirituality"],
    slug: "power-of-rituals"
  }, {
    title: "Mindfulness – A Daily Practice of Presence",
    excerpt: "Learn how mindfulness can be integrated into daily life through simple practices that cultivate presence, clarity, and balance.",
    image: "/lovable-uploads/eb2e3123-3b3d-403c-accb-13d1268e4b02.png",
    date: "July 12, 2025",
    readTime: "5 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Wellness"],
    slug: "mindfulness-daily"
  }, {
    title: "Why Burnout Affects More and More People",
    excerpt: "Explore the growing burnout epidemic and discover practical pathways to prevention and recovery in our modern work culture.",
    image: "/lovable-uploads/c1bf03f6-871e-48eb-87e6-22d0c017354f.png",
    date: "July 5, 2025",
    readTime: "6 min read",
    author: "Piotr",
    categories: ["Mental Health", "Wellness"],
    slug: "burnout-epidemic"
  }, {
    title: "Relationships Without Screens – How Technology Changed Our Meetings",
    excerpt: "Discover how technology impacts our connections and practical ways to reclaim meaningful face-to-face relationships in a digital world.",
    image: "/lovable-uploads/5b506b4d-137c-47a7-bdb0-a29725093fbd.png",
    date: "June 28, 2025",
    readTime: "7 min read",
    author: "Piotr",
    categories: ["Digital Wellness", "Community"],
    slug: "relationships-without-screens"
  }, {
    title: "Breath – The Forgotten Tool of Regeneration",
    excerpt: "Discover how conscious breathing can restore calm and energy. Learn practical techniques backed by science to transform your daily life.",
    image: "/lovable-uploads/1d5b7aa6-1cf8-434e-b7b2-cd7f5630d998.png",
    date: "June 21, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Wellness Practices", "Mindfulness"],
    slug: "breath-regeneration"
  }, {
    title: "The Power of Nature – Why the Forest Heals",
    excerpt: "Discover the scientific evidence behind nature's healing power and practical ways to reconnect with the natural world for better health and well-being.",
    image: "/lovable-uploads/f21790ee-4570-4fde-a8c7-61222d63453d.png",
    date: "June 14, 2025",
    readTime: "9 min read",
    author: "Piotr",
    categories: ["Nature Practices", "Wellness"],
    slug: "power-of-nature"
  }, {
    title: "Slow Life – The Philosophy That Changes Everyday Life",
    excerpt: "Discover how the slow life movement from Italy offers a conscious response to modern acceleration, creating space for presence and meaning.",
    image: "/lovable-uploads/5a51808f-6165-41aa-8676-40e235829648.png",
    date: "June 7, 2025",
    readTime: "10 min read",
    author: "Piotr",
    categories: ["Lifestyle", "Mindfulness"],
    slug: "slow-life"
  }, {
    title: "Digital Detox – Why Our Brain Needs Silence",
    excerpt: "Discover the science behind digital overwhelm and practical steps to reclaim your attention and well-being through conscious disconnection.",
    image: "/lovable-uploads/83fe081b-c262-4d29-b438-860b88fdcd93.png",
    date: "June 1, 2025",
    readTime: "8 min read",
    author: "Piotr",
    categories: ["Mindfulness", "Digital Wellness"],
    slug: "digital-detox"
  }];

  // Get all unique categories from articles
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    articles.forEach(article => {
      article.categories.forEach(cat => cats.add(cat));
    });
    return ["All", ...Array.from(cats).sort()];
  }, []);

  // Filter and sort articles
  const filteredAndSortedArticles = useMemo(() => {
    let filtered = selectedCategory === "All" ? articles : articles.filter(article => article.categories.includes(selectedCategory));

    // Sort articles
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      } else if (sortBy === "readTime") {
        const aTime = parseInt(a.readTime);
        const bTime = parseInt(b.readTime);
        return aTime - bTime;
      } else {
        return a.title.localeCompare(b.title);
      }
    });
    return sorted;
  }, [selectedCategory, sortBy]);

  // Featured article is always the newest (first in array)
  const featuredArticle = articles[0];
  return <>
      <SEO title="Echoes of the Forest - Mystic Forest Blog" description="Explore articles about mindful living, digital wellness, nature connection, and the art of slowing down in our modern world." canonicalUrl="https://mysticforest.club/blog" />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-serif font-light mb-6 text-foreground leading-tight">
              Echoes of the Forest
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Explore articles about mindful living, nature connection, and the art of slowing down in our modern world.
            </p>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed mt-4">Co-created by human curiosity and a calm, mindful AI.
Proof that even technology can learn the art of stillness. 🤖🌿</p>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-light mb-4 text-foreground">Featured Article</h2>
            <div className="w-24 h-px bg-border"></div>
          </div>
          
          <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer" onClick={() => handleArticleClick(featuredArticle.slug)}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative overflow-hidden">
                <img src={featuredArticle.image} alt={featuredArticle.title} className="w-full h-80 md:h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 grayscale" />
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {featuredArticle.categories.map((cat, idx) => <Badge key={idx} className="bg-accent/90 text-accent-foreground border border-accent-foreground/20">
                      {cat}
                    </Badge>)}
                </div>
              </div>
              
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="mb-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {featuredArticle.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredArticle.readTime}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Piotr
                    </div>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-serif font-medium text-foreground mb-4 group-hover:text-accent transition-colors duration-300">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                    {featuredArticle.excerpt}
                  </p>
                </div>
                
                <div className="flex flex-col gap-4">
                  <Button className="w-fit group/btn">
                    Read Article
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                  
                  <SocialShare url={featuredArticle.slug === 'romo-relief' || featuredArticle.slug === 'overstimulation' || featuredArticle.slug === 'offline-gatherings' || featuredArticle.slug === 'lost-art-of-walking' ? `https://mysticforest.club/articles/${featuredArticle.slug}` : `https://mysticforest.club/blog/${featuredArticle.slug}`} title={featuredArticle.title} description={featuredArticle.excerpt} />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Category Filter & Sort */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
            <div className="flex flex-wrap gap-2 justify-center">
              {allCategories.map(category => <Button key={category} variant={category === selectedCategory ? "default" : "outline"} className="rounded-full" onClick={() => setSelectedCategory(category)}>
                  {category}
                </Button>)}
            </div>
            
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
              <select value={sortBy} onChange={e => setSortBy(e.target.value as "date" | "readTime" | "title")} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent">
                <option value="date">Newest First</option>
                <option value="readTime">Shortest First</option>
                <option value="title">Alphabetical</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-light mb-4 text-foreground">Recent Articles</h2>
            <div className="w-24 h-px bg-border"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAndSortedArticles.length === 0 ? <div className="col-span-full text-center py-16">
                <Leaf className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-serif text-muted-foreground mb-2">No Articles Found</h3>
                <p className="text-muted-foreground">Try selecting a different category.</p>
              </div> : filteredAndSortedArticles.map((article, index) => <Card key={index} className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer" onClick={() => handleArticleClick(article.slug)}>
                  <div className="relative overflow-hidden">
                    <img src={article.image} alt={article.title} className="w-full h-56 object-cover object-center group-hover:scale-105 transition-transform duration-700 grayscale" />
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                      {article.categories.map((cat, idx) => <Badge key={idx} className="bg-accent/90 text-accent-foreground text-xs border border-accent-foreground/20">
                          {cat}
                        </Badge>)}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {article.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-serif font-medium text-foreground mb-3 group-hover:text-accent transition-colors duration-300 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {article.author}
                      </span>
                      <Button variant="ghost" size="sm" className="text-xs p-2 h-auto text-foreground hover:text-accent hover:bg-transparent">
                        Read More <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                    
                    <div className="border-t pt-4">
                      <SocialShare url={article.slug === 'romo-relief' || article.slug === 'overstimulation' || article.slug === 'offline-gatherings' || article.slug === 'lost-art-of-walking' ? `https://mysticforest.club/articles/${article.slug}` : `https://mysticforest.club/blog/${article.slug}`} title={article.title} description={article.excerpt} />
                    </div>
                  </div>
                </Card>)}
          </div>
          
          <div className="text-center mt-16">
            <Button variant="outline" size="lg" className="px-8">
              Load More Articles
            </Button>
          </div>
        </div>
      </section>


        <Footer />
      </div>
    </>;
};
export default Blog;